import { Tag } from 'src/ts/@types/generated/graphql/catalog-service/graphql';

export type TagFilter = Pick<
  Tag,
  'category' | 'displayName' | 'displayValue' | 'value' | 'id'
>;
export interface NavLinkInterface {
  displayText: string;
  url: string;
  iconName?: string;
  target?: string;
}

export interface FooterLinkSection {
  header?: string;
  links?: NavLinkInterface[];
}

interface SocialLink {
  hrefUrl: string;
  logoAssetUrl: string;
}

export type InterpolatedLinkCopy = (string | NavLinkInterface)[];

interface MetaLink {
  rel: string;
  href: string;
  sizes?: string;
}

interface MetaTag {
  name?: string;
  content: string;
  property?: string;
}

export interface ImageSet {
  sm: string;
  md: string;
  lg: string;
}
/**
 * There's 4 options for tenants to configure subscriptions:
 * 1. 'only-single': Only single deliveries are handled, so recurring is set to false when creating a cart, and toggle is hidden
 * 2. 'only-subscription': Only subscriptions are handled, so recurring is set to true when creating a cart, and toggle is hidden
 * 3. 'both-subscription-on': Both single deliveries and subscription are handled. Also recurring is set to true when creating a cart, and toggle is shown
 * 4. 'both-subscription-off': Both single deliveries and subscription are handled. Also recurring is set to false when creating a cart, and toggle is shown
 */
export type SubscriptionToggleConfigurationInterface =
  | 'only-single'
  | 'only-subscription'
  | 'both-subscription-on'
  | 'both-subscription-off';

export type TenantConfigTag = TagFilter & {
  id?: number;
  category?: {
    id?: number;
    name?: string;
  };
};
export type VisibleTagFilter = {
  preselected?: boolean;
  description?: string;
  displayAs?: string;
  tag: TenantConfigTag;
  showInFunnel?: boolean;
  showInCustomizer?: boolean;
};

export interface UnfilteredConfigObject {
  navBar: {
    logoImgUrl: string;
    logoLinkUrl?: string;
    authenticated: {
      leftLinks: NavLinkInterface[];
      rightLinks: NavLinkInterface[];
      userMenuLinks: NavLinkInterface[];
    };
    unAuthenticated: {
      leftLinks: NavLinkInterface[];
      rightLinks: NavLinkInterface[];
      logIn: NavLinkInterface;
    };
  };
  footer: {
    linkSections: FooterLinkSection[];
    contactInfoSection: {
      // @todo after code change for https://territory-foods.atlassian.net/browse/PLAT-670 is
      // deployed to prod we can make this just string[]
      address: string | string[];
      phoneNumber: string;
      email: string;
      socialLinks?: SocialLink[];
    };
    watermark?: string; // eg @Territory Foods 2021
  };
  homeUrl: string;
  landingPageUrl: string;
  getStartedRedirectUrl?: string;
  meta: {
    title: string;
    metaLinks: MetaLink[];
    metaTags: MetaTag[];
  };
  getStartedPage: {
    aboutYouImg?: ImageSet;
    title?: string;
    subtitle?: string;
    form?: {
      title?: string;
      subtitle?: string;
      info?: string;
    };
    showHowItWorks?: boolean;
    termsAndPrivacy: InterpolatedLinkCopy;
  };
  menuPage: {
    carouselImages: Pick<ImageSet, 'sm' | 'lg'>[];
    menuEntryModalConfig?: {
      version?: 'ZIP' | 'EMAIL_ZIP' | 'FULL';
      title?: string;
      subtitle?: string;
    };
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editMealsPage: Record<string, any>;
  confirmationPage: {
    heroImage: Pick<ImageSet, 'sm' | 'lg'> & { textColor: string };
    // interpolated into copy instructing user to look for confirmation email. will default to `us` if not provided
    orderSender?: string;
  };
  loadingImgUrl: string;
  dashboardPage?: {
    heroImage?: Pick<ImageSet, 'sm' | 'lg'>;
  };
  /**
   * Links referenced multiple places throughout the app that can be referenced directly
   */
  globalLinks: {
    termsOfUse: NavLinkInterface;
    privacyPolicy: NavLinkInterface;
  };
  subscriptionToggleConfiguration?: SubscriptionToggleConfigurationInterface;
  mealDetailProfile: {
    showMacros: boolean;
    showSupplier: boolean;
    showPrice: boolean;
    useNetCarbs?: boolean;
  };
  showIngredientStandards?: boolean;
  visibleTagFilters: VisibleTagFilter[];
  clientSideKeys: {
    recaptchaSiteKey: string;
    segmentKey: string;
    segmentKeyStg: string;
    gtmKey?: string;
    zendeskKey?: string;
  };
  paymentSettings?: {
    paypalEnabled: boolean;
  };
  enableAllergenWarning?: boolean;
  hidePrimaryProteinFilter?: boolean;
  standingNewCustomerDiscountCode?: string;
  tenantRedirect?: {
    redirectUrl: string;
    redirectTitle: string;
    redirectCopy: string;
    buttonCopy: string;
  };
  defaultLoadingCatalogZipcode?: string;
}

/**
 * List of top-level keys that are permitted to flow through to the frontend.
 *
 * Using `as const` gets us better typing below; while the strings in the array
 * themselves are not type checked agains config keys, if you put an incorrect value in here,
 * you'll get an error below when doing the Pick type for AllowListConfig.
 */
export const ALLOWED_KEYS = [
  'clientSideKeys',
  'confirmationPage',
  'dashboardPage',
  'defaultLoadingCatalogZipcode',
  'editMealsPage',
  'enableAllergenWarning',
  'footer',
  'getStartedPage',
  'getStartedRedirectUrl',
  'globalLinks',
  'hidePrimaryProteinFilter',
  'homeUrl',
  'landingPageUrl',
  'loadingImgUrl',
  'mealDetailProfile',
  'menuPage',
  'meta',
  'navBar',
  'showIngredientStandards',
  'standingNewCustomerDiscountCode',
  'subscriptionToggleConfiguration',
  'visibleTagFilters',
  'paymentSettings',
  'tenantRedirect',
] as const;

type AllowedKeysTuple = typeof ALLOWED_KEYS;

type AllowedKeys = AllowedKeysTuple[number];

/**
 * Type for allow values that can pass through to the client.
 *
 * While more cumbersome to use a Pick than an Omit, it's safer to explicitly have
 * to opt valuesin to the allow list than to prohibit values.
 *
 */
export type AllowListConfig = Pick<UnfilteredConfigObject, AllowedKeys>;

/**
 * The added `name` is the domain name, referenced in the FE code.
 * The featuredMeals fields are not from the config, but are added here for convenience.
 */
export type ClientSafeTenantConfig = AllowListConfig & {
  name: string;
  featuredMealsSkus: string[];
  featuredMealsTitle: string | null;
  featuredMealsSupportMessage: string | null;
  enableGiftNotes: boolean;
};
